import React, { useState, useEffect } from 'react';
import { FaCalendarAlt, FaComments, FaFileAlt, FaCog, FaCalendarDay, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';

const SchedulingForm = ({ category, onSubmit }) => {
  const [form, setForm] = useState({
    schedulingCategory: category,
    dateOfIssue: new Date().toISOString().split('T')[0],
    involvedTeam: '',
    errorType: '',
    description: '',
    impact: 'No Impact',
    correction: '',
    recurrenceRisk: 'Low'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const update = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      onSubmit(form);
      setIsSubmitting(false);
    }, 100);
  };

  // Get icon based on category
  const getCategoryIcon = () => {
    if (category.includes('Appointment')) return FaCalendarAlt;
    if (category.includes('Communication')) return FaComments;
    if (category.includes('Documentation')) return FaFileAlt;
    if (category.includes('System') || category.includes('Process')) return FaCog;
    return FaExclamationTriangle; // Default icon
  };

  const CategoryIcon = getCategoryIcon();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex size-16 items-center justify-center rounded-full bg-indigo-100">
            <CategoryIcon className="size-8 text-indigo-600" aria-hidden="true" />
          </div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            {category}
          </h1>
          <p className="mt-2 text-base text-gray-600">
            Report scheduling or communication errors and document corrective actions
          </p>
        </div>

        {/* Form Card */}
        <div className="overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-gray-900/5">
          <form onSubmit={submit}>
            <div className="space-y-12 px-6 py-8 sm:px-10 sm:py-10">
              {/* Incident Details Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaCalendarDay className="size-5 text-gray-400" />
                  Incident Details
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  When the error occurred and who was involved
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="dateOfIssue" className="block text-sm/6 font-medium text-gray-900">
                      Date of Issue <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="dateOfIssue"
                        name="dateOfIssue"
                        type="date"
                        value={form.dateOfIssue}
                        onChange={update}
                        required
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="involvedTeam" className="block text-sm/6 font-medium text-gray-900">
                      Team/Department
                    </label>
                    <div className="mt-2">
                      <input
                        id="involvedTeam"
                        name="involvedTeam"
                        type="text"
                        value={form.involvedTeam}
                        onChange={update}
                        placeholder="Enter team or department"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Error Classification Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaExclamationTriangle className="size-5 text-gray-400" />
                  Error Classification
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Classify the type and impact of the error
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="errorType" className="block text-sm/6 font-medium text-gray-900">
                      Error Type
                    </label>
                    <div className="mt-2">
                      <select
                        id="errorType"
                        name="errorType"
                        value={form.errorType}
                        onChange={update}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      >
                        <option value="">Select error type</option>
                        <option>Appointment</option>
                        <option>Communication</option>
                        <option>Documentation</option>
                        <option>System/Process</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="impact" className="block text-sm/6 font-medium text-gray-900">
                      Impact
                    </label>
                    <div className="mt-2">
                      <select
                        id="impact"
                        name="impact"
                        value={form.impact}
                        onChange={update}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      >
                        <option>No Impact</option>
                        <option>Delay</option>
                        <option>Reschedule</option>
                        <option>Cancellation</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="recurrenceRisk" className="block text-sm/6 font-medium text-gray-900">
                      Recurrence Risk
                    </label>
                    <div className="mt-2">
                      <select
                        id="recurrenceRisk"
                        name="recurrenceRisk"
                        value={form.recurrenceRisk}
                        onChange={update}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      >
                        <option>Low</option>
                        <option>Moderate</option>
                        <option>High</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Error Details Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaFileAlt className="size-5 text-gray-400" />
                  Error Details
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Describe the error and any corrective actions taken
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8">
                  <div>
                    <label htmlFor="description" className="block text-sm/6 font-medium text-gray-900">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="description"
                        name="description"
                        rows={6}
                        value={form.description}
                        onChange={update}
                        required
                        placeholder="Provide a detailed description of the scheduling or communication error..."
                        className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="correction" className="block text-sm/6 font-medium text-gray-900">
                      Correction/Prevention
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="correction"
                        name="correction"
                        rows={4}
                        value={form.correction}
                        onChange={update}
                        placeholder="Describe any corrective actions taken or preventive measures implemented..."
                        className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                    <p className="mt-2 text-sm text-gray-500">
                      Include steps taken to prevent similar errors in the future.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-center gap-x-4 border-t border-gray-200 px-6 py-4 sm:px-10">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center gap-x-2 rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-0.5 size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <FaCheckCircle aria-hidden="true" className="-ml-0.5 size-5" />
                    <span>Submit Form</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SchedulingForm;
